import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Keyboard, Zap, Trophy, Target, Clock, Star, AlertCircle, BookOpen, Dumbbell, Award, Hand, ChevronRight, Check, X } from 'lucide-react';
import { typingDojoAPI } from '../api';
import { toast } from 'sonner';

// ============ TYPING CURRICULUM DATA ============

const KEY_GROUPS = {
  homeRow: {
    name: "Home Row Runes",
    keys: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';'],
    leftHand: ['a', 's', 'd', 'f'],
    rightHand: ['j', 'k', 'l', ';'],
    description: "The foundation of typing. Your fingers rest here.",
    fingerGuide: {
      'a': 'Left Pinky',
      's': 'Left Ring',
      'd': 'Left Middle',
      'f': 'Left Index',
      'j': 'Right Index',
      'k': 'Right Middle',
      'l': 'Right Ring',
      ';': 'Right Pinky'
    }
  },
  topRow: {
    name: "Top Row Runes",
    keys: ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    leftHand: ['q', 'w', 'e', 'r', 't'],
    rightHand: ['y', 'u', 'i', 'o', 'p'],
    description: "Reach upward to master these keys.",
    fingerGuide: {
      'q': 'Left Pinky', 'w': 'Left Ring', 'e': 'Left Middle', 'r': 'Left Index', 't': 'Left Index',
      'y': 'Right Index', 'u': 'Right Index', 'i': 'Right Middle', 'o': 'Right Ring', 'p': 'Right Pinky'
    }
  },
  bottomRow: {
    name: "Bottom Row Runes",
    keys: ['z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.'],
    leftHand: ['z', 'x', 'c', 'v', 'b'],
    rightHand: ['n', 'm', ',', '.'],
    description: "Reach downward to complete your training.",
    fingerGuide: {
      'z': 'Left Pinky', 'x': 'Left Ring', 'c': 'Left Middle', 'v': 'Left Index', 'b': 'Left Index',
      'n': 'Right Index', 'm': 'Right Index', ',': 'Right Middle', '.': 'Right Ring'
    }
  }
};

const PRACTICE_LEVELS = [
  { id: 1, name: "Single Key Runes", type: "single", patterns: ["fff", "jjj", "ddd", "kkk", "sss", "lll", "aaa", ";;;"], unlock: 0 },
  { id: 2, name: "Finger Pairs", type: "pairs", patterns: ["fj fj fj", "dk dk dk", "sl sl sl", "a; a; a;"], unlock: 1 },
  { id: 3, name: "Home Row Forms", type: "forms", patterns: ["asdf", "jkl;", "fdsa", ";lkj", "asdfjkl;"], unlock: 2 },
  { id: 4, name: "Simple Words", type: "words", patterns: ["sad", "fad", "lad", "ask", "dad", "fall", "all", "lass"], unlock: 3 },
  { id: 5, name: "Top Row Introduction", type: "single", patterns: ["eee", "iii", "rrr", "uuu", "www", "ooo"], unlock: 4 },
  { id: 6, name: "Mixed Row Words", type: "words", patterns: ["the", "are", "was", "were", "said", "like", "word"], unlock: 5 },
  { id: 7, name: "Bottom Row Introduction", type: "single", patterns: ["ccc", "mmm", "nnn", "vvv", "bbb", "zzz"], unlock: 6 },
  { id: 8, name: "Full Keyboard Words", type: "words", patterns: ["jump", "quick", "brown", "lazy", "fox", "zebra"], unlock: 7 }
];

// Visual keyboard component
const VirtualKeyboard = ({ highlightKey, pressedKey, showFingers = true }) => {
  const rows = [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';'],
    ['z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '/']
  ];
  
  const fingerColors = {
    'Left Pinky': '#EF4444',
    'Left Ring': '#F97316',
    'Left Middle': '#EAB308',
    'Left Index': '#22C55E',
    'Right Index': '#22C55E',
    'Right Middle': '#EAB308',
    'Right Ring': '#F97316',
    'Right Pinky': '#EF4444'
  };
  
  const getKeyInfo = (key) => {
    for (const group of Object.values(KEY_GROUPS)) {
      if (group.fingerGuide[key]) {
        return { finger: group.fingerGuide[key], color: fingerColors[group.fingerGuide[key]] };
      }
    }
    return { finger: null, color: '#64748B' };
  };
  
  return (
    <div className="bg-slate-900/80 p-4 rounded-xl">
      <div className="space-y-2">
        {rows.map((row, rowIdx) => (
          <div key={rowIdx} className="flex justify-center gap-1" style={{ paddingLeft: rowIdx === 1 ? '20px' : rowIdx === 2 ? '40px' : '0' }}>
            {row.map((key) => {
              const { finger, color } = getKeyInfo(key);
              const isHighlighted = highlightKey?.toLowerCase() === key;
              const isPressed = pressedKey?.toLowerCase() === key;
              const isHomeKey = ['f', 'j'].includes(key);
              
              return (
                <div
                  key={key}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center font-mono text-lg transition-all ${
                    isPressed ? 'scale-95 bg-green-500 text-white' :
                    isHighlighted ? 'bg-indigo-500 text-white scale-105 ring-2 ring-indigo-400 animate-pulse' :
                    'bg-slate-800 text-slate-300'
                  }`}
                  style={showFingers && !isHighlighted && !isPressed ? { borderBottom: `3px solid ${color}` } : {}}
                >
                  <span>{key.toUpperCase()}</span>
                  {isHomeKey && <div className="absolute w-1 h-1 bg-white/50 rounded-full -bottom-0.5"></div>}
                </div>
              );
            })}
          </div>
        ))}
        {/* Spacebar */}
        <div className="flex justify-center mt-2">
          <div className={`w-48 h-8 rounded-lg flex items-center justify-center ${
            pressedKey === ' ' ? 'bg-green-500' : 'bg-slate-800'
          } text-slate-500 text-xs`}>
            SPACE
          </div>
        </div>
      </div>
      
      {/* Finger legend */}
      {showFingers && (
        <div className="mt-4 flex justify-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#EF4444' }}></div>
            <span className="text-slate-500">Pinky</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#F97316' }}></div>
            <span className="text-slate-500">Ring</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#EAB308' }}></div>
            <span className="text-slate-500">Middle</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#22C55E' }}></div>
            <span className="text-slate-500">Index</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ LEARN MODE COMPONENT ============
const LearnMode = ({ onBack, onComplete }) => {
  const [currentLesson, setCurrentLesson] = useState(0);
  const [lessonStep, setLessonStep] = useState(0);
  const [pressedKey, setPressedKey] = useState(null);
  
  const lessons = [
    {
      title: "The Home Row Runes",
      description: "Place your fingers on the home row. This is where your fingers will always return.",
      steps: [
        { type: 'intro', content: "Your left hand fingers rest on A, S, D, F", highlight: ['a', 's', 'd', 'f'] },
        { type: 'intro', content: "Your right hand fingers rest on J, K, L, ;", highlight: ['j', 'k', 'l', ';'] },
        { type: 'intro', content: "Notice F and J have bumps to help you find them without looking!", highlight: ['f', 'j'] },
        { type: 'practice', content: "Press F with your left index finger", target: 'f' },
        { type: 'practice', content: "Press J with your right index finger", target: 'j' },
        { type: 'practice', content: "Now try: f j f j", sequence: ['f', 'j', 'f', 'j'] }
      ]
    },
    {
      title: "Index Finger Mastery",
      description: "Your index fingers are the most important. They cover the most keys!",
      steps: [
        { type: 'intro', content: "Left index covers F, G, R, T, V, B", highlight: ['f', 'g', 'r', 't', 'v', 'b'] },
        { type: 'intro', content: "Right index covers J, H, Y, U, N, M", highlight: ['j', 'h', 'y', 'u', 'n', 'm'] },
        { type: 'practice', content: "Type: f r f t", sequence: ['f', 'r', 'f', 't'] },
        { type: 'practice', content: "Type: j u j y", sequence: ['j', 'u', 'j', 'y'] }
      ]
    },
    {
      title: "Middle Finger Path",
      description: "Your middle fingers reach for the top and bottom rows.",
      steps: [
        { type: 'intro', content: "Left middle: D, E, C", highlight: ['d', 'e', 'c'] },
        { type: 'intro', content: "Right middle: K, I, ,", highlight: ['k', 'i', ','] },
        { type: 'practice', content: "Type: d e d c", sequence: ['d', 'e', 'd', 'c'] },
        { type: 'practice', content: "Type: k i k", sequence: ['k', 'i', 'k'] }
      ]
    }
  ];
  
  const currentLessonData = lessons[currentLesson];
  const currentStep = currentLessonData?.steps[lessonStep];
  const [sequenceIndex, setSequenceIndex] = useState(0);
  
  useEffect(() => {
    const handleKeyPress = (e) => {
      const key = e.key.toLowerCase();
      setPressedKey(key);
      setTimeout(() => setPressedKey(null), 150);
      
      if (currentStep?.type === 'practice') {
        if (currentStep.target && key === currentStep.target) {
          // Single key correct
          toast.success('Correct!');
          nextStep();
        } else if (currentStep.sequence) {
          if (key === currentStep.sequence[sequenceIndex]) {
            if (sequenceIndex === currentStep.sequence.length - 1) {
              toast.success('Sequence complete!');
              setSequenceIndex(0);
              nextStep();
            } else {
              setSequenceIndex(prev => prev + 1);
            }
          } else {
            toast.error('Try again');
            setSequenceIndex(0);
          }
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentStep, sequenceIndex]);
  
  const nextStep = () => {
    if (lessonStep < currentLessonData.steps.length - 1) {
      setLessonStep(prev => prev + 1);
      setSequenceIndex(0);
    } else if (currentLesson < lessons.length - 1) {
      setCurrentLesson(prev => prev + 1);
      setLessonStep(0);
      setSequenceIndex(0);
      toast.success('Lesson complete! Moving to next...');
    } else {
      toast.success('All lessons complete!');
      onComplete?.();
    }
  };
  
  if (!currentStep) return null;
  
  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-slate-400 hover:text-white flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-sm text-slate-400">
          Lesson {currentLesson + 1} of {lessons.length} • Step {lessonStep + 1} of {currentLessonData.steps.length}
        </div>
      </div>
      
      {/* Lesson Header */}
      <div className="text-center">
        <h2 className="text-2xl font-philosopher text-white mb-2">{currentLessonData.title}</h2>
        <p className="text-slate-400">{currentLessonData.description}</p>
      </div>
      
      {/* Content */}
      <div className="bg-slate-900/50 p-6 rounded-xl text-center">
        <p className="text-xl text-white mb-4">{currentStep.content}</p>
        
        {currentStep.sequence && (
          <div className="flex justify-center gap-2 mb-4">
            {currentStep.sequence.map((key, idx) => (
              <span 
                key={idx}
                className={`w-10 h-10 rounded-lg flex items-center justify-center font-mono text-lg ${
                  idx < sequenceIndex ? 'bg-green-500 text-white' :
                  idx === sequenceIndex ? 'bg-indigo-500 text-white animate-pulse' :
                  'bg-slate-700 text-slate-400'
                }`}
              >
                {key.toUpperCase()}
              </span>
            ))}
          </div>
        )}
        
        {currentStep.type === 'intro' && (
          <button
            onClick={nextStep}
            className="mt-4 px-6 py-2 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 rounded-xl transition-all"
          >
            Continue <ChevronRight className="inline w-4 h-4" />
          </button>
        )}
      </div>
      
      {/* Keyboard */}
      <VirtualKeyboard 
        highlightKey={currentStep.target || currentStep.sequence?.[sequenceIndex]}
        pressedKey={pressedKey}
        showFingers={true}
      />
    </div>
  );
};

// ============ PRACTICE MODE COMPONENT ============
const PracticeMode = ({ onBack, progress, selectedChild, onDrillComplete }) => {
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [currentPatternIndex, setCurrentPatternIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [pressedKey, setPressedKey] = useState(null);
  const [practiceComplete, setPracticeComplete] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const inputRef = useRef(null);
  
  // Use highest_drill for practice unlock logic (not completed_levels from tests)
  const highestDrill = progress?.highest_drill || 0;
  
  const startPractice = (practice) => {
    setSelectedPractice(practice);
    setCurrentPatternIndex(0);
    setUserInput('');
    setPracticeComplete(false);
    setCorrectCount(0);
    setTimeout(() => inputRef.current?.focus(), 100);
  };
  
  const handleInputChange = async (e) => {
    const value = e.target.value;
    setUserInput(value);
    
    if (!selectedPractice) return;
    
    const currentPattern = selectedPractice.patterns[currentPatternIndex];
    
    if (value === currentPattern) {
      setCorrectCount(prev => prev + 1);
      if (currentPatternIndex < selectedPractice.patterns.length - 1) {
        setCurrentPatternIndex(prev => prev + 1);
        setUserInput('');
        toast.success('Correct!');
      } else {
        setPracticeComplete(true);
        
        // Save drill completion to backend
        if (selectedChild) {
          try {
            const res = await typingDojoAPI.completeDrill(selectedChild.id, selectedPractice.id);
            if (res.data?.sparks_earned > 0) {
              toast.success(`Practice complete! +${res.data.sparks_earned} sparks!`);
            } else {
              toast.success('Practice complete!');
            }
            // Notify parent to refresh progress
            if (onDrillComplete) {
              onDrillComplete();
            }
          } catch (error) {
            console.error('Failed to save drill completion:', error);
            toast.success('Practice complete!');
          }
        } else {
          toast.success('Practice complete!');
        }
      }
    }
  };
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      setPressedKey(e.key);
      setTimeout(() => setPressedKey(null), 100);
    };
    
    if (selectedPractice) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedPractice]);
  
  if (practiceComplete) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Trophy className="w-10 h-10 text-green-400" />
        </div>
        <h2 className="text-2xl font-philosopher text-white mb-2">Practice Complete!</h2>
        <p className="text-slate-400 mb-6">You completed all {selectedPractice.patterns.length} patterns!</p>
        <div className="flex justify-center gap-4">
          <button
            onClick={() => startPractice(selectedPractice)}
            className="px-6 py-2 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 rounded-xl"
          >
            Practice Again
          </button>
          <button
            onClick={() => {
              setPracticeComplete(false);
              setSelectedPractice(null);
              setCurrentPatternIndex(0);
              setUserInput('');
              setCorrectCount(0);
            }}
            className="px-6 py-2 bg-slate-700/50 hover:bg-slate-700 text-white rounded-xl"
            data-testid="choose-another-btn"
          >
            Choose Another
          </button>
        </div>
      </div>
    );
  }
  
  if (selectedPractice) {
    const currentPattern = selectedPractice.patterns[currentPatternIndex];
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button onClick={() => setSelectedPractice(null)} className="text-slate-400 hover:text-white flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-sm text-slate-400">
            Pattern {currentPatternIndex + 1} of {selectedPractice.patterns.length}
          </div>
        </div>
        
        <div className="text-center">
          <h2 className="text-xl font-philosopher text-white mb-2">{selectedPractice.name}</h2>
          <p className="text-slate-500 text-sm">Type the pattern exactly as shown</p>
        </div>
        
        {/* Pattern display */}
        <div className="bg-slate-900/50 p-8 rounded-xl text-center">
          <div className="font-mono text-4xl tracking-widest mb-6">
            {currentPattern.split('').map((char, idx) => (
              <span 
                key={idx}
                className={
                  idx < userInput.length 
                    ? (userInput[idx] === char ? 'text-green-400' : 'text-red-400')
                    : idx === userInput.length 
                      ? 'text-white underline' 
                      : 'text-slate-500'
                }
              >
                {char === ' ' ? '\u00A0' : char}
              </span>
            ))}
          </div>
          
          <input
            ref={inputRef}
            type="text"
            value={userInput}
            onChange={handleInputChange}
            className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-center font-mono text-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Type here..."
            autoComplete="off"
          />
        </div>
        
        <VirtualKeyboard 
          highlightKey={currentPattern[userInput.length]}
          pressedKey={pressedKey}
          showFingers={true}
        />
      </div>
    );
  }
  
  // Practice level selection
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-slate-400 hover:text-white flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>
      
      <div className="text-center">
        <h2 className="text-2xl font-philosopher text-white mb-2">Finger Path Training</h2>
        <p className="text-slate-400">Build muscle memory with targeted practice drills</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PRACTICE_LEVELS.map((practice) => {
          // Check if drill is already completed or if previous drill is completed
          const isCompleted = (progress?.completed_drills || []).includes(practice.id);
          const isLocked = practice.unlock > highestDrill;
          
          return (
            <button
              key={practice.id}
              onClick={() => {
                if (!isLocked) {
                  startPractice(practice);
                }
              }}
              disabled={isLocked}
              data-testid={`drill-${practice.id}`}
              className={`p-4 rounded-xl text-left transition-all ${
                isLocked 
                  ? 'bg-slate-900/30 opacity-50 cursor-not-allowed' 
                  : isCompleted
                    ? 'bg-green-900/20 border border-green-500/30 hover:border-green-500/50 cursor-pointer'
                    : 'bg-slate-900/50 hover:bg-slate-800/50 border border-slate-800 hover:border-indigo-500/30 cursor-pointer'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-semibold">{practice.name}</span>
                {isLocked ? (
                  <span className="text-xs text-slate-500">Complete drill {practice.unlock} first</span>
                ) : isCompleted ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Dumbbell className="w-4 h-4 text-indigo-400" />
                )}
              </div>
              <div className="flex flex-wrap gap-1">
                {practice.patterns.slice(0, 3).map((p, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-slate-800 text-slate-400 text-xs rounded font-mono">
                    {p}
                  </span>
                ))}
                {practice.patterns.length > 3 && (
                  <span className="text-xs text-slate-500">+{practice.patterns.length - 3} more</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============ TEST MODE COMPONENT (Original Typing Dojo) ============
const TestMode = ({ onBack, selectedChild }) => {
  const [levels, setLevels] = useState([]);
  const [progress, setProgress] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [gameState, setGameState] = useState('menu');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  const [wordsTyped, setWordsTyped] = useState(0);
  const [correctChars, setCorrectChars] = useState(0);
  const [totalChars, setTotalChars] = useState(0);
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [levelsRes, progressRes] = await Promise.all([
        typingDojoAPI.getLevels(),
        typingDojoAPI.getProgress(selectedChild.id).catch(() => ({ data: null }))
      ]);
      setLevels(levelsRes.data || []);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error fetching typing data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const startLevel = async (level) => {
    try {
      const res = await typingDojoAPI.getChallenge(level);
      if (!res.data?.words?.length) {
        toast.error('Challenge not available');
        return;
      }
      
      setChallenge(res.data);
      setSelectedLevel(level);
      setCurrentWordIndex(0);
      setUserInput('');
      setWordsTyped(0);
      setCorrectChars(0);
      setTotalChars(0);
      setTimeLeft(res.data.time_limit || 60);
      setGameState('playing');
      startTimeRef.current = Date.now();
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (error) {
      toast.error('Could not start challenge');
    }
  };
  
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      timerRef.current = setTimeout(() => setTimeLeft(t => t - 1), 1000);
    } else if (timeLeft === 0 && gameState === 'playing') {
      endGame();
    }
    return () => clearTimeout(timerRef.current);
  }, [gameState, timeLeft]);
  
  const endGame = async () => {
    clearTimeout(timerRef.current);
    setGameState('results');
    
    const elapsedMinutes = (Date.now() - startTimeRef.current) / 1000 / 60;
    const wpm = elapsedMinutes > 0 ? Math.round(wordsTyped / elapsedMinutes) : 0;
    const accuracy = totalChars > 0 ? correctChars / totalChars : 0;
    
    setResults({ wpm, accuracy, wordsTyped });
    
    try {
      const res = await typingDojoAPI.submitResult(selectedChild.id, selectedLevel, wpm, accuracy, wordsTyped);
      if (res.data?.passed) {
        toast.success(`Level passed! +${res.data.sparks_earned} sparks!`);
      }
      fetchData();
    } catch (error) {
      console.error('Error submitting result:', error);
    }
  };
  
  const handleInputChange = (e) => {
    if (gameState !== 'playing' || !challenge) return;
    
    const value = e.target.value;
    const currentWord = challenge.words[currentWordIndex];
    
    setUserInput(value);
    
    // For sentence-level challenges (level 5 Master only), don't auto-advance on space
    // User must type the full sentence. Level 4 (Scholar) still uses words.
    const isSentenceMode = selectedLevel >= 5;
    
    if (isSentenceMode) {
      // Only advance when full sentence matches
      if (value === currentWord) {
        // Calculate accuracy for this sentence
        setCorrectChars(prev => prev + currentWord.length);
        setTotalChars(prev => prev + currentWord.length);
        setWordsTyped(prev => prev + currentWord.split(' ').length);
        
        if (currentWordIndex < challenge.words.length - 1) {
          setCurrentWordIndex(prev => prev + 1);
          setUserInput('');
        } else {
          endGame();
        }
      }
    } else {
      // Word-by-word mode (levels 1-3)
      if (value.endsWith(' ') || value === currentWord) {
        const typedWord = value.trim();
        // Calculate character-level accuracy for this word
        const wordLength = currentWord.length;
        let correctInWord = 0;
        for (let i = 0; i < Math.min(typedWord.length, wordLength); i++) {
          if (typedWord[i] === currentWord[i]) {
            correctInWord++;
          }
        }
        
        setCorrectChars(prev => prev + correctInWord);
        setTotalChars(prev => prev + wordLength);
        
        if (typedWord === currentWord) {
          setWordsTyped(prev => prev + 1);
        }
        
        if (currentWordIndex < challenge.words.length - 1) {
          setCurrentWordIndex(prev => prev + 1);
          setUserInput('');
        } else {
          endGame();
        }
      }
    }
  };
  
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-indigo-500 rounded-full mx-auto"></div>
      </div>
    );
  }
  
  if (gameState === 'results') {
    return (
      <div className="text-center py-8">
        <Trophy className="w-16 h-16 text-amber-400 mx-auto mb-4" />
        <h2 className="text-2xl font-philosopher text-white mb-4">Dojo Trial Complete!</h2>
        
        <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-6">
          <div className="bg-slate-900/50 p-4 rounded-xl">
            <Zap className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{results?.wpm || 0}</p>
            <p className="text-xs text-slate-400">WPM</p>
          </div>
          <div className="bg-slate-900/50 p-4 rounded-xl">
            <Target className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{Math.round((results?.accuracy || 0) * 100)}%</p>
            <p className="text-xs text-slate-400">Accuracy</p>
          </div>
          <div className="bg-slate-900/50 p-4 rounded-xl">
            <Star className="w-6 h-6 text-indigo-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{results?.wordsTyped || 0}</p>
            <p className="text-xs text-slate-400">Words</p>
          </div>
        </div>
        
        <div className="flex justify-center gap-4">
          <button
            onClick={() => startLevel(selectedLevel)}
            className="px-6 py-2 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 rounded-xl"
          >
            Try Again
          </button>
          <button
            onClick={() => setGameState('menu')}
            className="px-6 py-2 bg-slate-700/50 hover:bg-slate-700 text-white rounded-xl"
          >
            Back to Trials
          </button>
        </div>
      </div>
    );
  }
  
  if (gameState === 'playing') {
    const currentWord = challenge?.words?.[currentWordIndex] || '';
    
    return (
      <div className="space-y-6">
        {/* Timer and stats */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 ${timeLeft <= 10 ? 'text-red-400' : 'text-white'}`}>
              <Clock className="w-5 h-5" />
              <span className="font-mono text-xl">{timeLeft}s</span>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-slate-400">Words: <span className="text-white">{wordsTyped}</span></span>
            <span className="text-slate-400">WPM: <span className="text-white">
              {Math.round(wordsTyped / ((60 - timeLeft) / 60) || 0)}
            </span></span>
          </div>
        </div>
        
        {/* Word/Sentence display */}
        <div className="bg-slate-900/50 p-8 rounded-xl text-center">
          {selectedLevel >= 5 ? (
            <>
              {/* Sentence mode (level 5 Master) */}
              <p className="text-xs text-indigo-400 mb-2">Type the full sentence</p>
              <div className="mb-4 max-w-2xl mx-auto">
                <span className="text-xl font-mono text-white leading-relaxed">{currentWord}</span>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                {currentWordIndex + 1} / {challenge?.words?.length || 0} sentences
              </p>
            </>
          ) : (
            <>
              {/* Word mode */}
              <div className="mb-4">
                <span className="text-3xl font-mono text-white">{currentWord}</span>
              </div>
            </>
          )}
          
          <input
            ref={inputRef}
            type="text"
            value={userInput}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              // For sentence mode (level 5 Master), allow Enter to submit current sentence
              if (e.key === 'Enter' && selectedLevel >= 5) {
                e.preventDefault();
                if (userInput.trim() === challenge.words[currentWordIndex]) {
                  // Correct - advance
                  setCorrectChars(prev => prev + challenge.words[currentWordIndex].length);
                  setTotalChars(prev => prev + challenge.words[currentWordIndex].length);
                  setWordsTyped(prev => prev + challenge.words[currentWordIndex].split(' ').length);
                  
                  if (currentWordIndex < challenge.words.length - 1) {
                    setCurrentWordIndex(prev => prev + 1);
                    setUserInput('');
                  } else {
                    endGame();
                  }
                }
              }
            }}
            className={`w-full ${selectedLevel >= 5 ? 'max-w-2xl' : 'max-w-md'} bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-center font-mono ${selectedLevel >= 5 ? 'text-base' : 'text-xl'} text-white focus:outline-none focus:ring-2 focus:ring-indigo-500`}
            placeholder={selectedLevel >= 5 ? "Type the sentence and press Enter..." : "Type here..."}
            autoComplete="off"
            autoFocus
          />
          
          {/* Upcoming words - only for word mode (levels 1-4) */}
          {selectedLevel < 5 && (
            <div className="mt-4 text-slate-500 text-sm">
              {challenge?.words?.slice(currentWordIndex + 1, currentWordIndex + 5).join(' • ')}
            </div>
          )}
        </div>
        
        <button
          onClick={endGame}
          className="w-full py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-xl"
        >
          End Trial Early
        </button>
      </div>
    );
  }
  
  // Level selection menu
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-slate-400 hover:text-white flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {progress && (
          <div className="text-sm text-slate-400">
            Best WPM: <span className="text-white">{progress.best_wpm || 0}</span>
          </div>
        )}
      </div>
      
      <div className="text-center">
        <h2 className="text-2xl font-philosopher text-white mb-2">Dojo Trials</h2>
        <p className="text-slate-400">Test your typing speed and accuracy</p>
      </div>
      
      <div className="grid gap-4">
        {levels.map((level, idx) => {
          const isUnlocked = progress?.completed_levels >= idx || idx === 0;
          const isCompleted = progress?.completed_levels > idx;
          
          return (
            <button
              key={level.level}
              onClick={() => isUnlocked && startLevel(level.level)}
              disabled={!isUnlocked}
              className={`p-4 rounded-xl text-left transition-all ${
                !isUnlocked ? 'bg-slate-900/30 opacity-50' :
                isCompleted ? 'bg-green-500/10 border border-green-500/30' :
                'bg-slate-900/50 border border-slate-800 hover:border-indigo-500/30'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-white font-semibold">{level.name}</span>
                  <p className="text-sm text-slate-500">{level.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  {isCompleted && <Check className="w-5 h-5 text-green-400" />}
                  <span className="text-xs text-amber-400">+{level.sparks_reward} sparks</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============ MAIN TYPING DOJO PAGE ============
const TypingDojoPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('menu'); // menu, learn, practice, test
  const [progress, setProgress] = useState(null);
  
  const selectedChild = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('selectedChild') || 'null');
    } catch {
      return null;
    }
  }, []);
  
  useEffect(() => {
    if (!selectedChild) {
      navigate('/child-select');
      return;
    }
    fetchProgress();
  }, [selectedChild]);
  
  const fetchProgress = async () => {
    try {
      const res = await typingDojoAPI.getProgress(selectedChild.id);
      setProgress(res.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };
  
  if (!selectedChild) return null;
  
  return (
    <div className="min-h-screen bg-[#0F172A] relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
      
      <div className="relative z-10 py-8 px-4 max-w-4xl mx-auto">
        {/* Header */}
        {mode === 'menu' && (
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/temple')}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              data-testid="back-to-temple"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Temple</span>
            </button>
            
            <div className="flex items-center gap-2">
              <Hand className="w-5 h-5 text-amber-400" />
              <span className="text-white font-philosopher">Typing Dojo</span>
            </div>
          </div>
        )}
        
        {/* Mode selection menu */}
        {mode === 'menu' && (
          <div className="space-y-8">
            <div className="text-center">
              <h1 className="text-4xl font-philosopher font-bold text-white mb-2">
                Typing Dojo
              </h1>
              <p className="text-slate-400">Master the Key Runes and become a Temple Typing Master!</p>
            </div>
            
            {/* Progress summary */}
            {progress && (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-900/50 p-4 rounded-xl text-center">
                  <Zap className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{progress.best_wpm || 0}</p>
                  <p className="text-xs text-slate-400">Best WPM</p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-xl text-center">
                  <Target className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{Math.round((progress.best_accuracy || 0) * 100)}%</p>
                  <p className="text-xs text-slate-400">Best Accuracy</p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-xl text-center">
                  <Trophy className="w-6 h-6 text-indigo-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{progress.completed_levels || 0}</p>
                  <p className="text-xs text-slate-400">Levels Complete</p>
                </div>
              </div>
            )}
            
            {/* Mode cards */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Learn Mode */}
              <button
                onClick={() => setMode('learn')}
                className="glass-panel p-6 rounded-2xl text-left hover:border-emerald-500/30 transition-all group"
                data-testid="learn-mode-btn"
              >
                <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Learn Mode</h3>
                <p className="text-slate-400 text-sm mb-3">Key Runes Training</p>
                <p className="text-slate-500 text-xs">
                  Learn proper finger placement and master the keyboard one key at a time.
                </p>
              </button>
              
              {/* Practice Mode */}
              <button
                onClick={() => setMode('practice')}
                className="glass-panel p-6 rounded-2xl text-left hover:border-amber-500/30 transition-all group"
                data-testid="practice-mode-btn"
              >
                <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Dumbbell className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Practice Mode</h3>
                <p className="text-slate-400 text-sm mb-3">Finger Path Drills</p>
                <p className="text-slate-500 text-xs">
                  Build muscle memory with single keys, pairs, patterns, and words.
                </p>
              </button>
              
              {/* Test Mode */}
              <button
                onClick={() => setMode('test')}
                className="glass-panel p-6 rounded-2xl text-left hover:border-indigo-500/30 transition-all group"
                data-testid="test-mode-btn"
              >
                <div className="w-14 h-14 bg-indigo-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Award className="w-7 h-7 text-indigo-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Test Mode</h3>
                <p className="text-slate-400 text-sm mb-3">Dojo Trials</p>
                <p className="text-slate-500 text-xs">
                  Timed typing tests that measure WPM and accuracy. Earn sparks!
                </p>
              </button>
            </div>
          </div>
        )}
        
        {/* Learn Mode */}
        {mode === 'learn' && (
          <LearnMode 
            onBack={() => setMode('menu')} 
            onComplete={() => {
              toast.success('Learning complete! Try Practice mode next.');
              setMode('menu');
            }}
          />
        )}
        
        {/* Practice Mode */}
        {mode === 'practice' && (
          <PracticeMode 
            onBack={() => setMode('menu')} 
            progress={progress}
            selectedChild={selectedChild}
            onDrillComplete={fetchProgress}
          />
        )}
        
        {/* Test Mode */}
        {mode === 'test' && (
          <TestMode 
            onBack={() => setMode('menu')} 
            selectedChild={selectedChild}
          />
        )}
      </div>
    </div>
  );
};

export default TypingDojoPage;
